<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { FairCapsule } from '@/ui/patterns/FairCapsule'
import { FairAiTip } from '@/ui/primitives/FairAiTip'
import { FairButton } from '@/ui/primitives/FairButton'
import { FairCarouselIndicator } from '@/ui/primitives/FairCarouselIndicator'
import { FairProgressSpinner } from '@/ui/primitives/FairProgressSpinner'
import { useHomeCapsulesPreviewQuery } from '@/queries/capsules'
import type { Audience } from '@/features/recommendations/api/recommendations'
import {
  FAIR_HOME_CAPSULES_AI_TIP_KEY,
  FAIR_HOME_CAPSULES_CARD_GAP,
  FAIR_HOME_CAPSULES_DEFAULT_AUDIENCE,
  FAIR_HOME_CAPSULES_DEFAULT_LIMIT,
  clampFairHomeCapsulesCarouselPage,
  getFairHomeCapsuleLookItems,
  getFairHomeCapsulesCardWidth,
  getFairHomeCapsulesCarouselOffset,
  getFairHomeCapsulesCarouselPageCount,
  getFairHomeCapsulesCardsPerView,
  resolveFairHomeCapsuleSlides,
} from './fair-home-capsules.data'
import './fair-home-capsules.css'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const carouselPage = ref(1)
const lookIndexByCapsuleId = reactive<Record<number, number>>({})
const viewportWidth = ref(
  typeof window !== 'undefined' ? window.innerWidth : 1920,
)
const carouselViewportRef = ref<HTMLElement | null>(null)

/** Skip scroll→page sync while we programmatically scroll (arrows / dots). */
let programmaticScroll = false
let programmaticScrollTimer: ReturnType<typeof setTimeout> | null = null

const audience = computed<Audience>(() => {
  const value = route.query.audience
  if (Array.isArray(value)) {
    return (value[0] ?? FAIR_HOME_CAPSULES_DEFAULT_AUDIENCE) as Audience
  }
  return (value ?? FAIR_HOME_CAPSULES_DEFAULT_AUDIENCE) as Audience
})

const capsulesQuery = useHomeCapsulesPreviewQuery(
  computed(() => ({
    limit: FAIR_HOME_CAPSULES_DEFAULT_LIMIT,
    audience: audience.value,
  })),
)

const slides = computed(() =>
  resolveFairHomeCapsuleSlides(capsulesQuery.data.value),
)

const isLoading = computed(
  () => capsulesQuery.isLoading.value && capsulesQuery.data.value == null,
)

const cardWidth = computed(() =>
  getFairHomeCapsulesCardWidth(viewportWidth.value),
)

const cardsPerView = computed(() =>
  getFairHomeCapsulesCardsPerView(
    viewportWidth.value,
    cardWidth.value,
    FAIR_HOME_CAPSULES_CARD_GAP,
  ),
)

const carouselPageCount = computed(() =>
  getFairHomeCapsulesCarouselPageCount(slides.value.length, cardsPerView.value),
)

const safeCarouselPage = computed(() =>
  clampFairHomeCapsulesCarouselPage(
    carouselPage.value,
    carouselPageCount.value,
  ),
)

const sectionStyle = computed(() => ({
  '--fair-home-capsules-card-width': `${cardWidth.value}px`,
}))

const aiTipLabel = computed(() =>
  t(`fair_home.capsules.ai_tips.${FAIR_HOME_CAPSULES_AI_TIP_KEY}`),
)

function getLookIndex(capsuleId: number): number {
  return lookIndexByCapsuleId[capsuleId] ?? 0
}

function setLookIndex(capsuleId: number, index: number, totalLooks: number) {
  if (totalLooks < 1) return
  const safe = ((index % totalLooks) + totalLooks) % totalLooks
  lookIndexByCapsuleId[capsuleId] = safe
}

function stepPx(): number {
  return cardWidth.value + FAIR_HOME_CAPSULES_CARD_GAP
}

function scrollToPage(page: number, behavior: ScrollBehavior = 'smooth') {
  const el = carouselViewportRef.value
  if (!el) return

  const next = clampFairHomeCapsulesCarouselPage(page, carouselPageCount.value)
  carouselPage.value = next

  programmaticScroll = true
  if (programmaticScrollTimer) clearTimeout(programmaticScrollTimer)

  el.scrollTo({
    left: getFairHomeCapsulesCarouselOffset(
      next,
      cardWidth.value,
      FAIR_HOME_CAPSULES_CARD_GAP,
    ),
    behavior,
  })

  programmaticScrollTimer = setTimeout(() => {
    programmaticScroll = false
    programmaticScrollTimer = null
  }, behavior === 'smooth' ? 500 : 0)
}

function syncPageFromScroll() {
  const el = carouselViewportRef.value
  if (!el || programmaticScroll || !slides.value.length) return

  const index = Math.round(el.scrollLeft / stepPx())
  carouselPage.value = clampFairHomeCapsulesCarouselPage(
    index + 1,
    carouselPageCount.value,
  )
}

function handleResize() {
  viewportWidth.value = window.innerWidth
  const nextPage = clampFairHomeCapsulesCarouselPage(
    carouselPage.value,
    getFairHomeCapsulesCarouselPageCount(
      slides.value.length,
      getFairHomeCapsulesCardsPerView(
        viewportWidth.value,
        getFairHomeCapsulesCardWidth(viewportWidth.value),
        FAIR_HOME_CAPSULES_CARD_GAP,
      ),
    ),
  )
  void nextTick(() => {
    scrollToPage(nextPage, 'auto')
  })
}

function handleCarouselPrev() {
  scrollToPage(safeCarouselPage.value - 1)
}

function handleCarouselNext() {
  scrollToPage(safeCarouselPage.value + 1)
}

function handleCarouselPageUpdate(page: number) {
  scrollToPage(page)
}

function handleCapsuleClick(capsuleId: number) {
  if (capsuleId < 0) return
  void router.push({
    name: 'capsule',
    params: { capsuleId: String(capsuleId) },
  })
}

function capsuleTo(capsuleId: number) {
  if (capsuleId < 0) return undefined
  return {
    name: 'capsule' as const,
    params: { capsuleId: String(capsuleId) },
  }
}

function handlePrevLook(capsuleId: number, totalLooks: number) {
  setLookIndex(capsuleId, getLookIndex(capsuleId) - 1, totalLooks)
}

function handleNextLook(capsuleId: number, totalLooks: number) {
  setLookIndex(capsuleId, getLookIndex(capsuleId) + 1, totalLooks)
}

watch(slides, () => {
  void nextTick(() => {
    scrollToPage(
      clampFairHomeCapsulesCarouselPage(
        carouselPage.value,
        carouselPageCount.value,
      ),
      'auto',
    )
  })
})

onMounted(() => {
  window.addEventListener('resize', handleResize, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  if (programmaticScrollTimer) clearTimeout(programmaticScrollTimer)
})
</script>

<template>
  <section
    class="fair-home-capsules"
    data-figma="1:8735"
    :style="sectionStyle"
  >
    <div class="fair-home-capsules__block">
      <header class="fair-home-capsules__header">
        <div class="fair-home-capsules__title-row">
          <h2 class="fair-home-capsules__title">
            {{ t('fair_home.capsules.title') }}
          </h2>
          <div class="fair-home-capsules__nav">
            <FairButton
              variant="outlined"
              icon-only
              leading-icon="arrow-left"
              rounded
              :aria-label="t('fair_home.capsules.carousel.prev')"
              :disabled="safeCarouselPage <= 1 || !slides.length"
              @click="handleCarouselPrev"
            />
            <FairButton
              variant="outlined"
              icon-only
              leading-icon="arrow-right"
              rounded
              :aria-label="t('fair_home.capsules.carousel.next')"
              :disabled="safeCarouselPage >= carouselPageCount || !slides.length"
              @click="handleCarouselNext"
            />
          </div>
        </div>
        <div class="fair-home-capsules__ai-tip-wrap">
          <FairAiTip
            :label="aiTipLabel"
            colored
            :show-button="false"
          />
        </div>
      </header>

      <div
        v-if="isLoading"
        class="fair-home-capsules__loading"
        role="status"
      >
        <FairProgressSpinner variant="basic" />
      </div>

      <template v-else-if="slides.length">
        <div
          ref="carouselViewportRef"
          class="fair-home-capsules__carousel-viewport"
          @scroll.passive="syncPageFromScroll"
        >
          <div class="fair-home-capsules__carousel-track">
            <div
              v-for="slide in slides"
              :key="slide.id"
              class="fair-home-capsules__card-slot"
            >
              <FairCapsule
                type="preview"
                size="L"
                device="desktop"
                :items="getFairHomeCapsuleLookItems(slide, getLookIndex(slide.id))"
                :name="slide.name"
                :description="slide.description"
                :current-look="getLookIndex(slide.id) + 1"
                :total-looks="slide.looks.length"
                :look-label="
                  t('fair_home.capsules.look_counter', {
                    current: getLookIndex(slide.id) + 1,
                    total: slide.looks.length,
                  })
                "
                :show-ai-tips="true"
                :interactive="true"
                :to="capsuleTo(slide.id)"
                @click="handleCapsuleClick(slide.id)"
                @prev-look="handlePrevLook(slide.id, slide.looks.length)"
                @next-look="handleNextLook(slide.id, slide.looks.length)"
              />
            </div>
          </div>
        </div>

        <div class="fair-home-capsules__dots">
          <FairCarouselIndicator
            :page="safeCarouselPage"
            :total="carouselPageCount"
            variant="stripes"
            tone="default"
            @update:page="handleCarouselPageUpdate"
          />
        </div>
      </template>

      <p
        v-else
        class="fair-home-capsules__empty"
      >
        {{ t('fair_home.capsules.empty') }}
      </p>
    </div>
  </section>
</template>
